import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, formatTicketRef, ScanResult } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft, MapPin, Tag, Calendar, QrCode, Download, Printer,
  Wrench, History, Ticket, Package,
  Edit, AlertCircle, Sparkles, CheckCircle,
  Circle, AlertTriangle, XCircle, X, RefreshCw,
  Move, Settings, Trash2, ExternalLink, ChevronDown, ChevronUp,
  Building2, FileText, Shield, Info, ChevronRight, Clock,
  Plus, UserPlus, Flag, MessageSquare, Truck, ArrowRightLeft,
  PlayCircle, PauseCircle, Euro, Loader2, ArrowRight, CalendarClock,
  ChevronLeft
} from 'lucide-react';
import QrScanner from '../components/QrScanner';
import { canPrint, printAssetLabel, isMobile } from '../lib/print';
import PCPrintButton, { ChangePrinterButton } from '../components/PCPrintButton';
import { ScheduledMaintenance } from '../lib/api';

// ============================================================================
// CONFIGURATIONS
// ============================================================================

const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  active: { label: 'Attivo', bg: 'bg-green-100', text: 'text-green-800', dot: 'bg-green-500' },
  in_repair: { label: 'In Riparazione', bg: 'bg-orange-100', text: 'text-orange-800', dot: 'bg-orange-500' },
  in_maintenance: { label: 'In Manutenzione', bg: 'bg-blue-100', text: 'text-blue-800', dot: 'bg-blue-500' },
  in_transit: { label: 'In Transito', bg: 'bg-purple-100', text: 'text-purple-800', dot: 'bg-purple-500' },
  in_storage: { label: 'In Magazzino', bg: 'bg-gray-100', text: 'text-gray-800', dot: 'bg-gray-500' },
  disposed: { label: 'Dismesso', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' },
  lost: { label: 'Perso', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' }
};

const conditionConfig: Record<string, { label: string; color: string; icon: any; emoji: string }> = {
  excellent: { label: 'Eccellente', color: 'text-green-600', icon: Sparkles, emoji: '✨' },
  good: { label: 'Buono', color: 'text-blue-600', icon: CheckCircle, emoji: '👍' },
  fair: { label: 'Discreto', color: 'text-yellow-600', icon: Circle, emoji: '👌' },
  poor: { label: 'Scarso', color: 'text-orange-600', icon: AlertTriangle, emoji: '⚠️' },
  broken: { label: 'Rotto', color: 'text-red-600', icon: XCircle, emoji: '❌' }
};

const eventTypeLabels: Record<string, string> = {
  moved: 'Spostato',
  status_changed: 'Cambio Stato',
  maintenance: 'Manutenzione',
  repair: 'Riparazione',
  created: 'Creato',
  updated: 'Aggiornato',
  disposed: 'Dismesso',
  location_change: 'Spostato',
  condition_change: 'Condizione',
  replacement: 'Sostituzione',
  note: 'Nota',
  ticket_created: 'Ticket Creato',
  ticket_status_changed: 'Stato Ticket',
  ticket_assigned: 'Assegnazione',
  ticket_priority_changed: 'Priorità Ticket',
  ticket_closed: 'Ticket Chiuso'
};

// Event type config with icons and colors for timeline
const eventTypeConfig: Record<string, { icon: any; color: string; bgColor: string }> = {
  moved: { icon: Truck, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  location_change: { icon: Truck, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  status_changed: { icon: ArrowRightLeft, color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
  maintenance: { icon: Settings, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  repair: { icon: Wrench, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  created: { icon: Plus, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  updated: { icon: Edit, color: 'text-gray-600', bgColor: 'bg-gray-100' },
  disposed: { icon: Trash2, color: 'text-red-600', bgColor: 'bg-red-100' },
  condition_change: { icon: Settings, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  replacement: { icon: RefreshCw, color: 'text-red-600', bgColor: 'bg-red-100' },
  note: { icon: MessageSquare, color: 'text-gray-600', bgColor: 'bg-gray-100' },
  ticket_created: { icon: Plus, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  ticket_status_changed: { icon: RefreshCw, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  ticket_assigned: { icon: UserPlus, color: 'text-purple-600', bgColor: 'bg-purple-100' },
  ticket_priority_changed: { icon: Flag, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  ticket_closed: { icon: CheckCircle, color: 'text-emerald-600', bgColor: 'bg-emerald-100' }
};

// Labels for ticket statuses (used in timeline)
const ticketStatusLabels: Record<string, string> = {
  open: 'Aperto',
  in_progress: 'In Lavorazione',
  waiting: 'In Attesa',
  resolved: 'Risolto',
  closed: 'Chiuso',
  cancelled: 'Annullato'
};

// Labels for asset statuses (used in timeline)
const assetStatusLabels: Record<string, string> = {
  active: 'Attivo',
  in_repair: 'In Riparazione',
  in_maintenance: 'In Manutenzione',
  in_transit: 'In Transito',
  in_storage: 'In Magazzino',
  disposed: 'Dismesso',
  lost: 'Perso'
};

// ============================================================================
// MODAL COMPONENT
// ============================================================================

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative inline-block w-full max-w-lg p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function AssetDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { modules, user } = useAuth();

  // Modal states
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [maintenanceModalOpen, setMaintenanceModalOpen] = useState(false);
  const [repairModalOpen, setRepairModalOpen] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [completeInterventionModalOpen, setCompleteInterventionModalOpen] = useState(false);
  const [showAllDetails, setShowAllDetails] = useState(false);
  const [scheduledMaintenanceModalOpen, setScheduledMaintenanceModalOpen] = useState(false);
  const [editingScheduledMaintenance, setEditingScheduledMaintenance] = useState<ScheduledMaintenance | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Form states - isCompleted defaults to false
  const [moveForm, setMoveForm] = useState({ locationId: '', notes: '', isCompleted: false });
  const [maintenanceForm, setMaintenanceForm] = useState({ description: '', newCondition: 'none', cost: 0, isCompleted: false });
  const [repairForm, setRepairForm] = useState({ description: '', newCondition: 'none', cost: 0, isCompleted: false });
  const [statusForm, setStatusForm] = useState({ status: '', condition: 'none', notes: '' });
  const [completeForm, setCompleteForm] = useState({ historyId: '', cost: 0, newCondition: 'none', actionType: '', description: '' });
  const [editCostModalOpen, setEditCostModalOpen] = useState(false);
  const [editCostForm, setEditCostForm] = useState({ historyId: '', cost: 0 });
  
  // Scheduled maintenance form
  const [scheduledMaintenanceForm, setScheduledMaintenanceForm] = useState({
    description: '',
    intervalMonths: 12,
    estimatedCost: '',
    referenceType: 'asset_creation' as 'asset_creation' | 'purchase_date' | 'custom',
    customReferenceDate: ''
  });
  
  // Move completion modal (like PendingActions)
  const [moveCompleteModalOpen, setMoveCompleteModalOpen] = useState(false);
  const [selectedMoveAction, setSelectedMoveAction] = useState<{
    historyId: string;
    toLocationId?: string;
    toLocationName?: string;
    toLocationQrCode?: string;
    assetName: string;
    assetQrCode: string;
    isOwnAction: boolean;
  } | null>(null);
  
  // QR scan completion states for move actions
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  const [scanStep, setScanStep] = useState<'asset' | 'location'>('location');
  const [assetVerified, setAssetVerified] = useState(false);
  const [assetScanSuccess, setAssetScanSuccess] = useState(false);
  const [scanningMoveAction, setScanningMoveAction] = useState<{
    historyId: string;
    toLocationId?: string;
    toLocationName?: string;
    toLocationQrCode?: string;
    assetQrCode?: string;
  } | null>(null);
  const [resultModal, setResultModal] = useState<{
    isOpen: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
    details?: string;
  }>({ isOpen: false, type: 'success', title: '', message: '' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['asset', id],
    queryFn: () => api.getAsset(id!),
    refetchOnMount: 'always'
  });

  const updateMutation = useMutation({
    mutationFn: (updateData: any) => api.updateAsset(id!, updateData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['asset', id] });
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      queryClient.invalidateQueries({ queryKey: ['pending-actions'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      
      // If completing a move via QR scan, show success modal
      if (scanningMoveAction && variables.action === 'completeIntervention') {
        setResultModal({
          isOpen: true,
          type: 'success',
          title: 'Spostamento Completato!',
          message: `L'asset è stato spostato con successo.`,
          details: scanningMoveAction.toLocationName ? `Nuova ubicazione: ${scanningMoveAction.toLocationName}` : undefined
        });
        setScanningMoveAction(null);
      }
      
      // Close modals
      setMoveModalOpen(false);
      setMaintenanceModalOpen(false);
      setRepairModalOpen(false);
      setStatusModalOpen(false);
      setCompleteInterventionModalOpen(false);
      // Reset forms
      setMoveForm({ locationId: '', notes: '', isCompleted: true });
      setMaintenanceForm({ description: '', newCondition: 'none', cost: 0, isCompleted: true });
      setRepairForm({ description: '', newCondition: 'none', cost: 0, isCompleted: true });
      setStatusForm({ status: '', condition: 'none', notes: '' });
      setCompleteForm({ historyId: '', cost: 0, newCondition: 'none', actionType: '', description: '' });
      setEditCostModalOpen(false);
      setEditCostForm({ historyId: '', cost: 0 });
    },
    onError: (error: Error) => {
      if (scanningMoveAction) {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Errore',
          message: 'Impossibile completare lo spostamento.',
          details: error.message || 'Si è verificato un errore durante il completamento.'
        });
      }
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteAsset(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assets'] });
      navigate('/assets');
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento asset...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Asset non trovato'}</span>
        </div>
      </div>
    );
  }

  const { asset, history, tickets, locations, hasOpenTicket, openTicketId, hasIncompleteAction, incompleteActionId, scheduledMaintenances } = data;
  const status = statusConfig[asset.status] || statusConfig.active;
  const condition = conditionConfig[asset.condition] || conditionConfig.good;

  // Find incomplete direct action details for UI (quick actions only, not ticket interventions)
  const incompleteAction = hasIncompleteAction 
    ? history.find((h: any) => h.id === incompleteActionId || (h.source === 'direct' && h.isCompleted === false))
    : null;

  // Filter locations for move dropdown (exclude current location)
  const availableLocations = locations.filter((loc: any) => loc.id !== asset.locationId);
  // Filter statuses for change status dropdown (exclude current status)
  const availableStatuses = Object.entries(statusConfig).filter(([key]) => key !== asset.status);
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(asset.qrCode)}`;

  // Action handlers
  const handleMove = () => {
    if (moveForm.locationId) {
      updateMutation.mutate({ 
        action: 'move', 
        locationId: moveForm.locationId, 
        notes: moveForm.notes,
        isCompleted: moveForm.isCompleted
      });
    }
  };

  const handleMaintenance = () => {
    if (maintenanceForm.description.trim()) {
      updateMutation.mutate({
        action: 'maintenance',
        description: maintenanceForm.description,
        newCondition: maintenanceForm.newCondition,
        cost: maintenanceForm.isCompleted ? maintenanceForm.cost : 0,
        isCompleted: maintenanceForm.isCompleted
      });
    }
  };

  const handleRepair = () => {
    if (repairForm.description.trim()) {
      updateMutation.mutate({
        action: 'repair',
        description: repairForm.description,
        newCondition: repairForm.newCondition,
        cost: repairForm.isCompleted ? repairForm.cost : 0,
        isCompleted: repairForm.isCompleted
      });
    }
  };

  const handleChangeStatus = () => {
    if (statusForm.status) {
      updateMutation.mutate({
        action: 'changeStatus',
        status: statusForm.status,
        condition: statusForm.condition,
        notes: statusForm.notes
      });
    }
  };

  const handleCompleteIntervention = () => {
    if (completeForm.historyId) {
      updateMutation.mutate({
        action: 'completeIntervention',
        historyId: completeForm.historyId,
        cost: completeForm.cost,
        newCondition: completeForm.newCondition,
        description: completeForm.description
      });
    }
  };

  const handleEditCost = () => {
    if (editCostForm.historyId) {
      updateMutation.mutate({
        action: 'updateHistoryCost',
        historyId: editCostForm.historyId,
        cost: editCostForm.cost
      });
    }
  };

  const handleAddScheduledMaintenance = () => {
    if (scheduledMaintenanceForm.description.trim() && scheduledMaintenanceForm.intervalMonths) {
      updateMutation.mutate({
        action: 'addScheduledMaintenance',
        description: scheduledMaintenanceForm.description.trim(),
        intervalMonths: scheduledMaintenanceForm.intervalMonths,
        estimatedCost: scheduledMaintenanceForm.estimatedCost || undefined,
        referenceType: scheduledMaintenanceForm.referenceType,
        customReferenceDate: scheduledMaintenanceForm.referenceType === 'custom' ? scheduledMaintenanceForm.customReferenceDate : undefined
      });
      setScheduledMaintenanceModalOpen(false);
      setScheduledMaintenanceForm({
        description: '',
        intervalMonths: 12,
        estimatedCost: '',
        referenceType: 'asset_creation',
        customReferenceDate: ''
      });
    }
  };

  const handleUpdateScheduledMaintenance = () => {
    if (editingScheduledMaintenance && scheduledMaintenanceForm.description.trim()) {
      updateMutation.mutate({
        action: 'updateScheduledMaintenance',
        maintenanceId: editingScheduledMaintenance.id,
        description: scheduledMaintenanceForm.description.trim(),
        intervalMonths: scheduledMaintenanceForm.intervalMonths,
        estimatedCost: scheduledMaintenanceForm.estimatedCost || undefined,
        referenceType: scheduledMaintenanceForm.referenceType,
        customReferenceDate: scheduledMaintenanceForm.referenceType === 'custom' ? scheduledMaintenanceForm.customReferenceDate : undefined
      });
      setScheduledMaintenanceModalOpen(false);
      setEditingScheduledMaintenance(null);
      setScheduledMaintenanceForm({
        description: '',
        intervalMonths: 12,
        estimatedCost: '',
        referenceType: 'asset_creation',
        customReferenceDate: ''
      });
    }
  };

  const handleDeleteScheduledMaintenance = (maintenanceId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa manutenzione programmata?')) {
      updateMutation.mutate({
        action: 'deleteScheduledMaintenance',
        maintenanceId
      });
    }
  };

  const openAddScheduledMaintenanceModal = () => {
    setEditingScheduledMaintenance(null);
    setScheduledMaintenanceForm({
      description: '',
      intervalMonths: 12,
      estimatedCost: '',
      referenceType: 'asset_creation',
      customReferenceDate: ''
    });
    setScheduledMaintenanceModalOpen(true);
  };

  const openEditScheduledMaintenanceModal = (maintenance: ScheduledMaintenance) => {
    setEditingScheduledMaintenance(maintenance);
    setScheduledMaintenanceForm({
      description: maintenance.description,
      intervalMonths: maintenance.intervalMonths,
      estimatedCost: maintenance.estimatedCost ? String(maintenance.estimatedCost / 100) : '',
      referenceType: maintenance.referenceType,
      customReferenceDate: maintenance.customReferenceDate 
        ? new Date(maintenance.customReferenceDate).toISOString().split('T')[0] 
        : ''
    });
    setScheduledMaintenanceModalOpen(true);
  };

  const openCompleteModal = (historyId: string, actionType?: string) => {
    // For move actions, show modal with QR scan option (like PendingActions)
    if (actionType === 'moved' || actionType === 'location_change') {
      // Find the history entry to get destination info
      const historyEntry = history.find((h: any) => h.id === historyId);
      
      // Check if current user started this action (comparing user IDs)
      const isOwnAction = historyEntry?.user?.id === user?.id;
      
      // Use data directly from history entry (now includes toLocationId, toLocationQrCode)
      setSelectedMoveAction({
        historyId,
        toLocationId: historyEntry?.toLocationId,
        toLocationName: historyEntry?.toLocation,
        toLocationQrCode: historyEntry?.toLocationQrCode,
        assetName: asset.name,
        assetQrCode: asset.qrCode,
        isOwnAction
      });
      setMoveCompleteModalOpen(true);
    } else {
      // For maintenance/repair, show modal to enter cost and edit description
      const historyEntry = history.find((h: any) => h.id === historyId);
      setCompleteForm({ 
        historyId, 
        cost: 0, 
        newCondition: 'none', 
        actionType: actionType || '',
        description: historyEntry?.description || ''
      });
      setCompleteInterventionModalOpen(true);
    }
  };

  // Start QR scan for move completion
  const startMoveQrScan = () => {
    if (!selectedMoveAction) return;
    
    // Set up scanning state
    setScanningMoveAction({
      historyId: selectedMoveAction.historyId,
      toLocationId: selectedMoveAction.toLocationId,
      toLocationName: selectedMoveAction.toLocationName,
      toLocationQrCode: selectedMoveAction.toLocationQrCode,
      assetQrCode: selectedMoveAction.assetQrCode
    });
    
    // If not the owner, need to scan asset first
    if (!selectedMoveAction.isOwnAction) {
      setScanStep('asset');
      setAssetVerified(false);
    } else {
      setScanStep('location');
      setAssetVerified(true);
    }
    
    setMoveCompleteModalOpen(false);
    setQrScannerOpen(true);
  };

  // Continue to location scan after asset verification
  const continueToLocationScan = () => {
    setAssetScanSuccess(false);
    setScanStep('location');
    setTimeout(() => setQrScannerOpen(true), 100);
  };

  // Complete move without QR scan
  const completeMoveDirectly = () => {
    if (!selectedMoveAction) return;
    
    updateMutation.mutate({
      action: 'completeIntervention',
      historyId: selectedMoveAction.historyId,
      cost: 0,
      newCondition: 'none'
    });
    setMoveCompleteModalOpen(false);
    setSelectedMoveAction(null);
  };

  // QR scan result handler for move completion
  const handleQrScanResult = async (result: ScanResult) => {
    if (!scanningMoveAction) return;
    
    // If not own action, need to scan asset first
    if (scanStep === 'asset') {
      if (result.found && result.type === 'asset') {
        // Check if scanned asset matches the expected asset
        if (result.qrCode?.toUpperCase() === scanningMoveAction.assetQrCode?.toUpperCase()) {
          // Asset verified - show success screen before proceeding to location
          setAssetVerified(true);
          setQrScannerOpen(false);
          setAssetScanSuccess(true);
        } else {
          setResultModal({
            isOpen: true,
            type: 'error',
            title: 'Asset Non Corretto',
            message: `Hai scansionato un asset diverso da quello atteso.`,
            details: `Asset atteso: ${asset.name} (${asset.qrCode})`
          });
          setQrScannerOpen(false);
        }
      } else if (result.found && result.type === 'location') {
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Scansione Non Valida',
          message: 'Devi prima scansionare il QR code dell\'asset per verificare di averlo in possesso.',
          details: `Asset da scansionare: ${asset.name} (${asset.qrCode})`
        });
        setQrScannerOpen(false);
      }
      return;
    }
    
    // Scanning for location
    if (result.found && result.type === 'location') {
      // Check if scanned location matches the expected destination
      if (scanningMoveAction.toLocationId && (
        result.qrCode?.toUpperCase() === scanningMoveAction.toLocationQrCode?.toUpperCase() ||
        result.id === scanningMoveAction.toLocationId
      )) {
        // Complete the move
        setQrScannerOpen(false);
        updateMutation.mutate({
          action: 'completeIntervention',
          historyId: scanningMoveAction.historyId,
          cost: 0,
          newCondition: 'none'
        });
      } else {
        // Wrong location
        setResultModal({
          isOpen: true,
          type: 'error',
          title: 'Ubicazione Non Corretta',
          message: 'L\'ubicazione scansionata non corrisponde alla destinazione prevista.',
          details: `Destinazione attesa: ${scanningMoveAction.toLocationName}`
        });
        setQrScannerOpen(false);
      }
    } else if (result.found && result.type === 'asset') {
      setResultModal({
        isOpen: true,
        type: 'error',
        title: 'Scansione Non Valida',
        message: 'Hai scansionato un asset invece di un\'ubicazione.',
        details: `Scansiona il QR code dell'ubicazione "${scanningMoveAction.toLocationName}" per completare lo spostamento.`
      });
      setQrScannerOpen(false);
    }
  };

  const openEditCostModal = (historyId: string, currentCost: number) => {
    setEditCostForm({ historyId, cost: currentCost / 100 });
    setEditCostModalOpen(true);
  };

  const handleDelete = () => {
    if (window.confirm('Sei sicuro di voler eliminare questo asset? L\'operazione non può essere annullata.')) {
      deleteMutation.mutate();
    }
  };

  const formatDate = (date: string | number | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateShort = (date: string | number | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatPrice = (cents: number | null) => {
    if (!cents) return null;
    return `€${(cents / 100).toFixed(2)}`;
  };

  const formatDateOnly = (date: string | number | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatIntervalMonths = (months: number) => {
    if (months === 1) return 'Ogni mese';
    if (months < 12) return `Ogni ${months} mesi`;
    const years = months / 12;
    if (years === 1) return 'Ogni anno';
    if (Number.isInteger(years)) return `Ogni ${years} anni`;
    return `Ogni ${months} mesi`;
  };

  const isMaintenanceOverdue = (nextDate: number) => nextDate < Date.now();
  const isMaintenanceSoon = (nextDate: number) => {
    const thirtyDaysFromNow = Date.now() + 30 * 24 * 60 * 60 * 1000;
    return nextDate <= thirtyDaysFromNow && nextDate >= Date.now();
  };

  // Check warranty status
  const isWarrantyExpired = asset.warrantyExpiresAt 
    ? new Date(asset.warrantyExpiresAt) < new Date() 
    : null;
  const isWarrantyExpiringSoon = asset.warrantyExpiresAt 
    ? new Date(asset.warrantyExpiresAt) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) && !isWarrantyExpired
    : false;

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Link to="/assets" className="icon-btn mt-1">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{asset.name}</h1>
            <p className="text-gray-400 font-mono text-sm mt-1">{asset.qrCode}</p>
          </div>
        </div>
        <Link
          to={`/assets/${id}/edit`}
          className="btn btn-secondary flex items-center gap-2"
        >
          <Edit className="w-4 h-4" />
          Modifica
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column (2/3) */}
        <div className="lg:col-span-2 space-y-6">
          {/* Asset Card with image placeholder and badges */}
          <div className="card p-0 overflow-hidden">
            {/* Status Banner */}
            <div className={`${status.bg} border-b ${status.bg.replace('bg-', 'border-')} px-4 py-3`}>
              <div className="flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white/80 ${status.text}`}>
                  <span className={`w-2 h-2 rounded-full ${status.dot}`}></span>
                  {status.label}
                </span>
                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium bg-white/80 ${condition.color}`}>
                  {condition.emoji} {condition.label}
                </span>
                {asset.category && (
                  <Link 
                    to={`/categories/${asset.category.id}`}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-sm font-medium bg-white/80 text-purple-700 hover:bg-white transition-colors"
                  >
                    <Tag className="w-3.5 h-3.5" />
                    {asset.category.name}
                  </Link>
                )}
              </div>
            </div>
            
            {/* Asset Image Area */}
            {(() => {
              const photos: string[] = asset.photoUrls ? JSON.parse(asset.photoUrls) : [];
              
              if (photos.length > 0) {
                return (
                  <div className="relative bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center min-h-64">
                    <img
                      src={photos[currentPhotoIndex]}
                      alt={`${asset.name} - Foto ${currentPhotoIndex + 1}`}
                      className="w-full max-h-80 object-contain"
                    />
                    {photos.length > 1 && (
                      <>
                        <button
                          onClick={() => setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => setCurrentPhotoIndex((prev) => (prev + 1) % photos.length)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                          {photos.map((_, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentPhotoIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-colors ${
                                idx === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              }
              
              return (
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 h-40 flex items-center justify-center">
                  <Package className="w-20 h-20 text-gray-200" />
                </div>
              );
            })()}
            
            {/* Quick Info */}
            {asset.description && (
              <div className="p-4 border-t border-gray-100">
                <p className="text-gray-600 text-sm leading-relaxed">{asset.description}</p>
              </div>
            )}
          </div>

          {/* Dettagli Principali */}
          <div className="card">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
              <FileText className="w-4 h-4 text-primary-500" />
              Informazioni Generali
            </h2>
            
            {/* Grid principale - sempre visibile - 2 cols on mobile, 3 on larger screens */}
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
              <div className="p-3 bg-gray-50 rounded-xl">
                <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                  <MapPin className="w-3.5 h-3.5" />
                  Ubicazione
                </dt>
                <dd className="text-gray-900 font-semibold text-sm lg:text-base">
                  {asset.location ? (
                    <Link to={`/locations/${asset.location.id}`} className="hover:text-primary-600 transition-colors">
                      {asset.location.name}
                    </Link>
                  ) : (
                    <span className="text-gray-400 font-normal">Non assegnato</span>
                  )}
                </dd>
              </div>
              
              {asset.brand && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                    <Building2 className="w-3.5 h-3.5" />
                    Marca
                  </dt>
                  <dd className="text-gray-900 font-semibold text-sm lg:text-base truncate">{asset.brand}</dd>
                </div>
              )}
              
              {asset.model && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                    <Package className="w-3.5 h-3.5" />
                    Modello
                  </dt>
                  <dd className="text-gray-900 font-semibold text-sm lg:text-base truncate">{asset.model}</dd>
                </div>
              )}
              
              {asset.serialNumber && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                    <FileText className="w-3.5 h-3.5" />
                    N. Seriale
                  </dt>
                  <dd className="text-gray-900 font-mono text-xs lg:text-sm font-medium truncate">{asset.serialNumber}</dd>
                </div>
              )}

              {asset.warrantyExpiresAt && (
                <div className="p-3 bg-gray-50 rounded-xl">
                  <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                    <Shield className="w-3.5 h-3.5" />
                    Garanzia
                  </dt>
                  <dd className={`font-semibold text-sm lg:text-base ${isWarrantyExpired ? 'text-red-600' : isWarrantyExpiringSoon ? 'text-yellow-600' : 'text-green-600'}`}>
                    {isWarrantyExpired ? 'Scaduta' : formatDateOnly(asset.warrantyExpiresAt)}
                  </dd>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded-xl">
                <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                  <Calendar className="w-3.5 h-3.5" />
                  Registrato
                </dt>
                <dd className="text-gray-900 font-semibold text-sm lg:text-base">{formatDateOnly(asset.createdAt)}</dd>
              </div>
            </div>

            {/* Dettagli espandibili */}
            {(asset.purchaseDate || asset.purchasePrice || asset.purchaseVendor || asset.invoiceNumber || asset.notes) && (
              <div className="mt-4 pt-4 border-t border-gray-100">
                <button
                  onClick={() => setShowAllDetails(!showAllDetails)}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
                >
                  {showAllDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showAllDetails ? 'Nascondi dettagli acquisto' : 'Mostra dettagli acquisto'}
                </button>

                {showAllDetails && (
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4 mt-4 animate-fadeIn">
                    {asset.purchaseDate && (
                      <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-xl border border-blue-100">
                        <dt className="text-xs text-blue-600 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                          <Calendar className="w-3.5 h-3.5" />
                          Data Acquisto
                        </dt>
                        <dd className="text-gray-900 font-semibold text-sm lg:text-base">{formatDateOnly(asset.purchaseDate)}</dd>
                      </div>
                    )}
                    
                    {asset.purchasePrice && (
                      <div className="p-3 bg-gradient-to-br from-green-50 to-green-100/50 rounded-xl border border-green-100">
                        <dt className="text-xs text-green-600 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                          <span>€</span>
                          Prezzo
                        </dt>
                        <dd className="text-gray-900 font-bold text-base lg:text-lg">{formatPrice(asset.purchasePrice)}</dd>
                      </div>
                    )}
                    
                    {asset.purchaseVendor && (
                      <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-xl border border-purple-100">
                        <dt className="text-xs text-purple-600 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                          <Building2 className="w-3.5 h-3.5" />
                          Fornitore
                        </dt>
                        <dd className="text-gray-900 font-semibold text-sm lg:text-base truncate">{asset.purchaseVendor}</dd>
                      </div>
                    )}
                    
                    {asset.invoiceNumber && (
                      <div className="p-3 bg-gray-50 rounded-xl">
                        <dt className="text-xs text-gray-500 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                          <FileText className="w-3.5 h-3.5" />
                          N. Fattura
                        </dt>
                        <dd className="text-gray-900 font-mono text-xs lg:text-sm font-medium truncate">{asset.invoiceNumber}</dd>
                      </div>
                    )}

                    {asset.notes && (
                      <div className="col-span-2 lg:col-span-full p-3 bg-amber-50/50 rounded-xl border border-amber-100">
                        <dt className="text-xs text-amber-600 mb-1 flex items-center gap-1.5 uppercase tracking-wide">
                          <Info className="w-3.5 h-3.5" />
                          Note
                        </dt>
                        <dd className="text-gray-700 text-sm leading-relaxed">{asset.notes}</dd>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Manutenzioni Programmate */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-cyan-500" />
                Manutenzioni Programmate
                {scheduledMaintenances && scheduledMaintenances.length > 0 && (
                  <span className="px-2 py-0.5 bg-cyan-100 text-cyan-600 rounded-full text-xs font-bold">
                    {scheduledMaintenances.length}
                  </span>
                )}
              </h2>
              <button
                onClick={openAddScheduledMaintenanceModal}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                + Aggiungi
              </button>
            </div>

            {!scheduledMaintenances || scheduledMaintenances.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <CalendarClock className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">Nessuna manutenzione programmata</p>
                <button
                  onClick={openAddScheduledMaintenanceModal}
                  className="mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  Aggiungi la prima manutenzione
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Descrizione</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Intervallo</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Prossima</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Costo</th>
                      <th className="py-2 px-2 w-20"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {scheduledMaintenances.map((maintenance: ScheduledMaintenance) => {
                      const isOverdue = isMaintenanceOverdue(maintenance.nextMaintenanceDate);
                      const isSoon = isMaintenanceSoon(maintenance.nextMaintenanceDate);
                      
                      return (
                        <tr 
                          key={maintenance.id}
                          className="border-b border-gray-50 hover:bg-gray-50 transition-colors"
                        >
                          <td className="py-2.5 px-3">
                            <div className="font-medium text-gray-900">{maintenance.description}</div>
                            <div className="text-xs text-gray-500 sm:hidden mt-0.5">
                              {formatIntervalMonths(maintenance.intervalMonths)}
                            </div>
                          </td>
                          <td className="py-2.5 px-3 hidden sm:table-cell">
                            <span className="text-xs text-gray-600">{formatIntervalMonths(maintenance.intervalMonths)}</span>
                          </td>
                          <td className="py-2.5 px-3">
                            <div className={`flex items-center gap-1.5 text-xs font-medium ${
                              isOverdue ? 'text-red-600' : isSoon ? 'text-amber-600' : 'text-gray-600'
                            }`}>
                              {isOverdue && <AlertTriangle className="w-3 h-3" />}
                              {isSoon && !isOverdue && <Clock className="w-3 h-3" />}
                              {formatDateOnly(maintenance.nextMaintenanceDate)}
                            </div>
                            {isOverdue && (
                              <span className="text-[10px] text-red-500 font-medium">Scaduta</span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 hidden sm:table-cell">
                            {maintenance.estimatedCost ? (
                              <span className="text-xs text-gray-600">€{(maintenance.estimatedCost / 100).toFixed(2)}</span>
                            ) : (
                              <span className="text-xs text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-2.5 px-2">
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => openEditScheduledMaintenanceModal(maintenance)}
                                className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                title="Modifica"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleDeleteScheduledMaintenance(maintenance.id)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="Elimina"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Ticket Correlati - Enterprise Table Style - Only shown when ticketing is enabled */}
          {modules.ticketingEnabled && (
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <Ticket className="w-4 h-4 text-orange-500" />
                Ticket Correlati
                {tickets.length > 0 && (
                  <span className="px-2 py-0.5 bg-orange-100 text-orange-600 rounded-full text-xs font-bold">
                    {tickets.length}
                  </span>
                )}
              </h2>
              <Link
                to={`/tickets/new?asset=${asset.id}`}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
              >
                + Nuovo
              </Link>
            </div>

            {tickets.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <Ticket className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">Nessun ticket per questo asset</p>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100">
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Rif.</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Titolo</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase">Stato</th>
                      <th className="text-left py-2 px-3 text-xs font-semibold text-gray-500 uppercase hidden sm:table-cell">Data</th>
                      <th className="py-2 px-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket: any) => {
                      const ticketStatusConfig: Record<string, { label: string; bg: string; text: string }> = {
                        open: { label: 'Aperto', bg: 'bg-amber-100', text: 'text-amber-700' },
                        in_progress: { label: 'In Corso', bg: 'bg-blue-100', text: 'text-blue-700' },
                        waiting: { label: 'In Attesa', bg: 'bg-orange-100', text: 'text-orange-700' },
                        resolved: { label: 'Risolto', bg: 'bg-green-100', text: 'text-green-700' },
                        closed: { label: 'Chiuso', bg: 'bg-gray-100', text: 'text-gray-700' }
                      };
                      const st = ticketStatusConfig[ticket.status] || ticketStatusConfig.open;
                      return (
                        <tr 
                          key={ticket.id}
                          onClick={() => navigate(`/tickets/${ticket.id}`)}
                          className="border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors"
                        >
                          <td className="py-2.5 px-3">
                            <span className="font-mono text-xs text-gray-500">{formatTicketRef(ticket.ticketNumber)}</span>
                          </td>
                          <td className="py-2.5 px-3 max-w-[200px]">
                            <span className="font-medium text-gray-900 truncate block">{ticket.title}</span>
                          </td>
                          <td className="py-2.5 px-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>
                              {st.label}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 hidden sm:table-cell">
                            <span className="text-xs text-gray-500 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateShort(ticket.createdAt)}
                            </span>
                          </td>
                          <td className="py-2.5 px-2">
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          )}

          {/* Cronologia Completa - Scrollabile */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                <History className="w-4 h-4 text-indigo-500" />
                Cronologia
                {history.length > 0 && (
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-600 rounded-full text-xs font-bold">
                    {history.length}
                  </span>
                )}
              </h2>
            </div>

            {history.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto bg-gray-100 rounded-xl flex items-center justify-center mb-3">
                  <History className="w-6 h-6 text-gray-300" />
                </div>
                <p className="text-gray-500 text-sm">Nessun evento registrato</p>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto pr-2">
                <div className="space-y-3">
                  {history.map((entry: any, index: number) => {
                    const config = eventTypeConfig[entry.type] || { icon: History, color: 'text-gray-600', bgColor: 'bg-gray-100' };
                    const EventIcon = config.icon;
                    const supportsCompletion = entry.type === 'repair' || entry.type === 'maintenance' || entry.type === 'moved' || entry.type === 'location_change';
                    const isMove = entry.type === 'moved' || entry.type === 'location_change';
                    const showCompleteButton = supportsCompletion && entry.isCompleted === false && entry.source === 'direct';
                    
                    return (
                      <div 
                        key={entry.id} 
                        className="flex gap-3 animate-slideInUp"
                        style={{ animationDelay: `${index * 30}ms` }}
                      >
                        {/* Timeline icon */}
                        <div className="flex flex-col items-center flex-shrink-0">
                          <div className={`w-9 h-9 rounded-xl ${config.bgColor} flex items-center justify-center`}>
                            <EventIcon className={`w-4 h-4 ${config.color}`} />
                          </div>
                          {index < history.length - 1 && (
                            <div className="w-0.5 flex-1 bg-gray-200 mt-2 min-h-[20px]"></div>
                          )}
                        </div>

                        {/* Event content */}
                        <div className="flex-1 pb-3 min-w-0">
                          <div className="bg-gray-50 rounded-xl p-3 hover:bg-gray-100 transition-colors">
                            {/* Header row with event type, badges, and Completa button (mobile) */}
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <span className={`font-semibold text-sm ${config.color}`}>
                                {eventTypeLabels[entry.type] || entry.type}
                              </span>
                              {/* Show completion status for repair/maintenance/move interventions */}
                              {supportsCompletion && entry.isCompleted === true && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" />
                                  Completato
                                </span>
                              )}
                              {supportsCompletion && entry.isCompleted === false && (
                                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">
                                  In corso
                                </span>
                              )}
                              {entry.source === 'direct' ? (
                                modules.ticketingEnabled && (
                                  <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded-full font-medium">
                                    Azione diretta
                                  </span>
                                )
                              ) : (
                                <Link
                                  to={`/tickets/${entry.ticketId}`}
                                  className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full hover:bg-blue-200 transition-colors flex items-center gap-1 font-medium"
                                >
                                  <Ticket className="w-3 h-3" />
                                  Ticket {entry.ticketNumber ? `#${entry.ticketNumber}` : ''}
                                </Link>
                              )}
                              {/* Complete button on same row as action type - mobile only */}
                              {showCompleteButton && (
                                <button
                                  onClick={() => openCompleteModal(entry.id, entry.type)}
                                  className="sm:hidden inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors ml-auto"
                                >
                                  <PlayCircle className="w-3 h-3" />
                                  Completa
                                </button>
                              )}
                              {/* Date only - right side on desktop (for non-move types) */}
                              {!isMove && (
                                <div className="hidden sm:flex flex-col items-end gap-1 ml-auto">
                                  <span className="text-xs text-gray-400">
                                    {formatDate(entry.createdAt)}
                                  </span>
                                  {showCompleteButton && (
                                    <button
                                      onClick={() => openCompleteModal(entry.id, entry.type)}
                                      className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors"
                                    >
                                      <PlayCircle className="w-3 h-3" />
                                      Completa
                                    </button>
                                  )}
                                </div>
                              )}
                              {/* Date only for moves on desktop - Completa button will be with location */}
                              {isMove && (
                                <span className="hidden sm:inline text-xs text-gray-400 ml-auto">
                                  {formatDate(entry.createdAt)}
                                </span>
                              )}
                            </div>
                            
                            {/* Date row - shown on mobile only (without Completa button, which is now in header) */}
                            <div className="flex sm:hidden items-center justify-between mb-2">
                              <time className="text-xs text-gray-400">
                                {formatDate(entry.createdAt)}
                              </time>
                            </div>
                            
                            {/* Move type - location and Completa on same row (desktop) */}
                            {isMove ? (
                              entry.fromLocation && entry.toLocation ? (
                                <div className="flex items-center gap-2 mt-2 flex-wrap">
                                  <span className="text-xs px-2 py-1 bg-indigo-100 text-indigo-700 rounded-lg font-medium">
                                    {entry.fromLocation} → {entry.toLocation}
                                  </span>
                                  {/* Desktop: Completa button next to location */}
                                  {showCompleteButton && (
                                    <button
                                      onClick={() => openCompleteModal(entry.id, entry.type)}
                                      className="hidden sm:inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors ml-auto"
                                    >
                                      <PlayCircle className="w-3 h-3" />
                                      Completa
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="mt-2">
                                  {entry.description && (
                                    <p className="text-sm text-gray-600">{entry.description}</p>
                                  )}
                                  {/* Desktop: Completa button for moves without location info */}
                                  {showCompleteButton && (
                                    <button
                                      onClick={() => openCompleteModal(entry.id, entry.type)}
                                      className="hidden sm:inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-800 font-medium bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded transition-colors mt-2"
                                    >
                                      <PlayCircle className="w-3 h-3" />
                                      Completa
                                    </button>
                                  )}
                                </div>
                              )
                            ) : entry.type === 'ticket_status_changed' ? (
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-lg font-medium">
                                  → {ticketStatusLabels[entry.toStatus] || entry.toStatus || 'N/A'}
                                </span>
                              </div>
                            ) : entry.type === 'status_changed' ? (
                              <div className="flex items-center gap-2 mt-2">
                                <span className="text-xs px-2 py-1 bg-cyan-100 text-cyan-700 rounded-lg font-medium">
                                  {entry.fromStatus ? `${assetStatusLabels[entry.fromStatus] || entry.fromStatus} → ` : ''}
                                  {assetStatusLabels[entry.toStatus] || entry.toStatus || 'N/A'}
                                </span>
                              </div>
                            ) : (entry.type === 'maintenance' || entry.type === 'repair') ? (
                              <div className="mt-2">
                                {entry.description && (
                                  <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
                                )}
                                <div className="flex flex-wrap items-center gap-2">
                                  {entry.cost ? (
                                    <button
                                      onClick={() => entry.source === 'direct' && openEditCostModal(entry.id, entry.cost)}
                                      className={`text-xs px-2 py-1 bg-green-100 text-green-700 rounded-lg font-medium flex items-center gap-1 ${entry.source === 'direct' ? 'hover:bg-green-200 cursor-pointer' : ''}`}
                                      disabled={entry.source !== 'direct'}
                                    >
                                      <Euro className="w-3 h-3" />
                                      {formatPrice(entry.cost)}
                                      {entry.source === 'direct' && <Edit className="w-3 h-3 ml-1 opacity-50" />}
                                    </button>
                                  ) : entry.source === 'direct' && entry.isCompleted && (
                                    <button
                                      onClick={() => openEditCostModal(entry.id, 0)}
                                      className="text-xs px-2 py-1 bg-gray-100 text-gray-500 rounded-lg font-medium flex items-center gap-1 hover:bg-gray-200 cursor-pointer"
                                    >
                                      <Euro className="w-3 h-3" />
                                      Aggiungi costo
                                    </button>
                                  )}
                                </div>
                              </div>
                            ) : entry.description && (
                              <p className="text-sm text-gray-600 mt-2">{entry.description}</p>
                            )}

                            {entry.user && (
                              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                                di {entry.user.firstName} {entry.user.lastName}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Right Column (1/3) - parte dall'alto */}
        <div className="flex flex-col space-y-6">
          {/* Azioni Rapide - First on mobile, second on desktop */}
          <div className="card order-1 lg:order-2">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4 flex items-center gap-2">
              <Wrench className="w-4 h-4 text-primary-500" />
              Azioni Rapide
            </h2>

            {/* Warning for incomplete direct action (quick actions) */}
            {hasIncompleteAction && incompleteAction && (
              <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <PauseCircle className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-amber-800 text-sm">Azione in corso</p>
                    <p className="text-xs text-amber-700 mt-1">
                      {eventTypeLabels[incompleteAction.type] || incompleteAction.type}: {incompleteAction.description?.substring(0, 50)}{incompleteAction.description?.length > 50 ? '...' : ''}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">
                      Completa l'azione prima di registrarne una nuova.
                    </p>
                    <button
                      onClick={() => openCompleteModal(incompleteActionId!, incompleteAction.type)}
                      className="inline-flex items-center gap-1.5 text-xs text-amber-800 hover:text-amber-900 font-semibold mt-2 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors"
                    >
                      <PlayCircle className="w-3.5 h-3.5" />
                      Completa Azione
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Warning for open ticket */}
            {hasOpenTicket && (
              <div className="bg-gradient-to-br from-yellow-50 to-amber-50 border border-yellow-200 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <AlertTriangle className="w-4 h-4 text-yellow-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-yellow-800 text-sm">Ticket aperto</p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Le azioni dirette sono disabilitate. Gestisci l'asset tramite il ticket.
                    </p>
                    <Link
                      to={`/tickets/${openTicketId}`}
                      className="inline-flex items-center gap-1 text-xs text-yellow-800 hover:text-yellow-900 font-semibold mt-2"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                      Vai al ticket
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-2">
              {/* Segnala Problema - Only visible when ticketing is enabled */}
              {modules.ticketingEnabled && (
                <Link
                  to={`/tickets/new?asset=${asset.id}`}
                  className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-br from-orange-100 to-amber-100 hover:from-orange-200 hover:to-amber-200 text-orange-700 border-orange-200 font-semibold"
                >
                  <AlertTriangle className="w-4 h-4" />
                  Segnala Problema
                </Link>
              )}

              {/* Action buttons - Disabled when ticket is open or incomplete action in progress */}
              <button
                onClick={() => {
                  setMoveForm({ locationId: '', notes: '', isCompleted: false });
                  setMoveModalOpen(true);
                }}
                disabled={hasOpenTicket || hasIncompleteAction || updateMutation.isPending}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-br from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100 text-green-700 border-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Move className="w-4 h-4" />
                Sposta Asset
              </button>

              <button
                onClick={() => {
                  setMaintenanceForm({ description: '', newCondition: 'none', cost: 0, isCompleted: false });
                  setMaintenanceModalOpen(true);
                }}
                disabled={hasOpenTicket || hasIncompleteAction || updateMutation.isPending}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-br from-teal-50 to-cyan-50 hover:from-teal-100 hover:to-cyan-100 text-teal-700 border-teal-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Settings className="w-4 h-4" />
                Registra Manutenzione
              </button>

              <button
                onClick={() => {
                  setRepairForm({ description: '', newCondition: 'none', cost: 0, isCompleted: false });
                  setRepairModalOpen(true);
                }}
                disabled={hasOpenTicket || hasIncompleteAction || updateMutation.isPending}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-br from-amber-50 to-orange-50 hover:from-amber-100 hover:to-orange-100 text-amber-700 border-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Wrench className="w-4 h-4" />
                Registra Riparazione
              </button>

              <button
                onClick={() => {
                  setStatusForm({ status: '', condition: 'none', notes: '' });
                  setStatusModalOpen(true);
                }}
                disabled={hasOpenTicket || hasIncompleteAction || updateMutation.isPending}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-br from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 text-purple-700 border-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RefreshCw className="w-4 h-4" />
                Cambia Stato
              </button>
            </div>
          </div>

          {/* Codice QR - Second on mobile, first on desktop */}
          <div className="card order-2 lg:order-1">
            <div className="flex items-center gap-2 mb-4">
              <QrCode className="w-4 h-4 text-primary-500" />
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Codice QR</h2>
            </div>
            <div className="flex flex-col items-center">
              <div className="bg-white p-3 rounded-xl border-2 border-gray-100 shadow-sm">
                <img
                  src={qrCodeUrl}
                  alt={`QR Code ${asset.qrCode}`}
                  className="w-44 h-44"
                />
              </div>
              <p className="text-gray-600 font-mono text-sm mt-3 px-3 py-1.5 bg-gray-100 rounded-lg">{asset.qrCode}</p>
              <div className="flex gap-2 w-full mt-3">
                <a
                  href={qrCodeUrl}
                  download={`${asset.qrCode}.png`}
                  className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Scarica
                </a>
                {canPrint() ? (
                  <button
                    onClick={() => {
                      const purchaseDateFormatted = asset.purchaseDate
                        ? new Date(asset.purchaseDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
                        : undefined;
                      printAssetLabel({
                        code: asset.qrCode,
                        qrUrl: asset.qrCode,
                        name: asset.name,
                        brand: asset.brand || undefined,
                        model: asset.model || undefined,
                        serialNumber: asset.serialNumber || undefined,
                        purchaseDate: purchaseDateFormatted,
                      });
                    }}
                    className="btn btn-primary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa Etichetta
                  </button>
                ) : !isMobile() ? (
                  <PCPrintButton
                    type="asset"
                    data={{
                      code: asset.qrCode,
                      qrContent: asset.qrCode,
                      name: asset.name,
                      brand: asset.brand || undefined,
                      model: asset.model || undefined,
                      serialNumber: asset.serialNumber || undefined,
                      purchaseDate: asset.purchaseDate
                        ? new Date(asset.purchaseDate).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
                        : undefined,
                    }}
                  />
                ) : (
                  <button
                    onClick={() => {
                      const printWindow = window.open('', '_blank');
                      if (!printWindow) return;
                      printWindow.document.write(`
                        <!DOCTYPE html>
                        <html>
                        <head>
                          <title>QR Code - ${asset.name}</title>
                          <style>
                            body {
                              font-family: Arial, sans-serif;
                              text-align: center;
                              padding: 40px;
                            }
                            .qr-container {
                              display: inline-block;
                              border: 2px solid #333;
                              padding: 20px;
                              border-radius: 12px;
                            }
                            .qr-code {
                              width: 200px;
                              height: 200px;
                            }
                            .asset-name {
                              font-size: 18px;
                              font-weight: bold;
                              margin-top: 15px;
                              color: #333;
                            }
                            .asset-code {
                              font-size: 12px;
                              color: #666;
                              margin-top: 5px;
                              font-family: monospace;
                            }
                            .asset-category {
                              font-size: 11px;
                              color: #888;
                              margin-top: 5px;
                            }
                            @media print {
                              body { padding: 20px; }
                            }
                          </style>
                        </head>
                        <body>
                          <div class="qr-container">
                            <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
                            <div class="asset-name">${asset.name}</div>
                            <div class="asset-code">${asset.qrCode}</div>
                            ${asset.category ? `<div class="asset-category">${asset.category.name}</div>` : ''}
                          </div>
                          <script>
                            window.onload = function() {
                              window.print();
                            }
                          </script>
                        </body>
                        </html>
                      `);
                      printWindow.document.close();
                    }}
                    className="btn btn-secondary text-sm flex-1 flex items-center justify-center gap-2"
                  >
                    <Printer className="w-4 h-4" />
                    Stampa
                  </button>
                )}
              </div>
              {canPrint() && (
                <button
                  onClick={() => {
                    const printWindow = window.open('', '_blank');
                    if (!printWindow) return;
                    printWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>QR Code - ${asset.name}</title>
                        <style>
                          body { font-family: Arial, sans-serif; text-align: center; padding: 40px; }
                          .qr-container { display: inline-block; border: 2px solid #333; padding: 20px; border-radius: 12px; }
                          .qr-code { width: 200px; height: 200px; }
                          .asset-name { font-size: 18px; font-weight: bold; margin-top: 15px; color: #333; }
                          .asset-code { font-size: 12px; color: #666; margin-top: 5px; font-family: monospace; }
                          @media print { body { padding: 20px; } }
                        </style>
                      </head>
                      <body>
                        <div class="qr-container">
                          <img src="${qrCodeUrl}" alt="QR Code" class="qr-code" />
                          <div class="asset-name">${asset.name}</div>
                          <div class="asset-code">${asset.qrCode}</div>
                        </div>
                        <script>window.onload = function() { window.print(); }</script>
                      </body>
                      </html>
                    `);
                    printWindow.document.close();
                  }}
                  className="text-xs text-gray-500 hover:text-gray-700 mt-2 underline cursor-pointer"
                >
                  Stampa da browser
                </button>
              )}
              {!isMobile() && <ChangePrinterButton />}
            </div>
          </div>

          {/* Zona Pericolo */}
          <div className="card border-red-200 order-3">
            <h2 className="text-lg font-semibold text-red-600 mb-4">Zona Pericolo</h2>
            <button
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="w-full btn flex items-center justify-center gap-2 bg-red-100 hover:bg-red-200 text-red-700 border-red-200 disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Elimina Asset
            </button>
          </div>
        </div>
      </div>

      {/* ============================================================================
          MODALS
          ============================================================================ */}

      {/* Move Modal */}
      <Modal isOpen={moveModalOpen} onClose={() => setMoveModalOpen(false)} title="Sposta Asset">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Nuova ubicazione <span className="text-red-500">*</span>
            </label>
            <select
              value={moveForm.locationId}
              onChange={(e) => setMoveForm({ ...moveForm, locationId: e.target.value })}
              className="input"
            >
              <option value="">Seleziona ubicazione...</option>
              {availableLocations.map((loc: any) => (
                <option key={loc.id} value={loc.id}>{loc.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Note (opzionale)</label>
            <textarea
              value={moveForm.notes}
              onChange={(e) => setMoveForm({ ...moveForm, notes: e.target.value })}
              className="input"
              rows={3}
              placeholder="Motivo dello spostamento..."
            />
          </div>
          <div className="form-group">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={moveForm.isCompleted}
                onChange={(e) => setMoveForm({ ...moveForm, isCompleted: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Spostamento completato</span>
            </label>
            {!moveForm.isCompleted && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                L'asset sarà impostato "In Transito" fino al completamento
              </p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setMoveModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleMove}
              disabled={!moveForm.locationId || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {moveForm.isCompleted ? 'Sposta' : 'Inizia Spostamento'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Maintenance Modal */}
      <Modal isOpen={maintenanceModalOpen} onClose={() => setMaintenanceModalOpen(false)} title="Registra Manutenzione">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Descrizione intervento <span className="text-red-500">*</span>
            </label>
            <textarea
              value={maintenanceForm.description}
              onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
              className="input"
              rows={3}
              placeholder="Descrivi l'intervento eseguito..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Condizione dopo intervento</label>
            <select
              value={maintenanceForm.newCondition}
              onChange={(e) => setMaintenanceForm({ ...maintenanceForm, newCondition: e.target.value })}
              className="input"
            >
              <option value="none">Nessun cambio</option>
              {Object.entries(conditionConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={maintenanceForm.isCompleted}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, isCompleted: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Manutenzione completata</span>
            </label>
            {!maintenanceForm.isCompleted && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                L'asset sarà impostato "In Manutenzione" fino al completamento
              </p>
            )}
          </div>
          {maintenanceForm.isCompleted && (
            <div className="form-group">
              <label className="form-label flex items-center gap-1">
                <Euro className="w-3.5 h-3.5" />
                Costo intervento (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={maintenanceForm.cost || ''}
                onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: parseFloat(e.target.value) || 0 })}
                className="input"
                placeholder="0.00"
              />
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setMaintenanceModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleMaintenance}
              disabled={!maintenanceForm.description.trim() || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {maintenanceForm.isCompleted ? 'Registra' : 'Inizia Manutenzione'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Repair Modal */}
      <Modal isOpen={repairModalOpen} onClose={() => setRepairModalOpen(false)} title="Registra Riparazione">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Descrizione intervento <span className="text-red-500">*</span>
            </label>
            <textarea
              value={repairForm.description}
              onChange={(e) => setRepairForm({ ...repairForm, description: e.target.value })}
              className="input"
              rows={3}
              placeholder="Descrivi l'intervento eseguito..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Condizione dopo intervento</label>
            <select
              value={repairForm.newCondition}
              onChange={(e) => setRepairForm({ ...repairForm, newCondition: e.target.value })}
              className="input"
            >
              <option value="none">Nessun cambio</option>
              {Object.entries(conditionConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={repairForm.isCompleted}
                onChange={(e) => setRepairForm({ ...repairForm, isCompleted: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm text-gray-700">Riparazione completata</span>
            </label>
            {!repairForm.isCompleted && (
              <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                L'asset sarà impostato "In Riparazione" fino al completamento
              </p>
            )}
          </div>
          {repairForm.isCompleted && (
            <div className="form-group">
              <label className="form-label flex items-center gap-1">
                <Euro className="w-3.5 h-3.5" />
                Costo intervento (€)
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={repairForm.cost || ''}
                onChange={(e) => setRepairForm({ ...repairForm, cost: parseFloat(e.target.value) || 0 })}
                className="input"
                placeholder="0.00"
              />
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setRepairModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleRepair}
              disabled={!repairForm.description.trim() || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {repairForm.isCompleted ? 'Registra' : 'Inizia Riparazione'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Change Status Modal */}
      <Modal isOpen={statusModalOpen} onClose={() => setStatusModalOpen(false)} title="Cambia Stato">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Nuovo stato <span className="text-red-500">*</span>
            </label>
            <select
              value={statusForm.status}
              onChange={(e) => setStatusForm({ ...statusForm, status: e.target.value })}
              className="input"
            >
              <option value="">Seleziona stato...</option>
              {availableStatuses.map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Nuova condizione</label>
            <select
              value={statusForm.condition}
              onChange={(e) => setStatusForm({ ...statusForm, condition: e.target.value })}
              className="input"
            >
              <option value="none">Nessun cambio</option>
              {Object.entries(conditionConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Note (opzionale)</label>
            <textarea
              value={statusForm.notes}
              onChange={(e) => setStatusForm({ ...statusForm, notes: e.target.value })}
              className="input"
              rows={3}
              placeholder="Motivo del cambio stato..."
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setStatusModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleChangeStatus}
              disabled={!statusForm.status || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              Aggiorna
            </button>
          </div>
        </div>
      </Modal>

      {/* Complete Action Modal (for maintenance/repair) */}
      <Modal isOpen={completeInterventionModalOpen} onClose={() => setCompleteInterventionModalOpen(false)} title="Completa Intervento">
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              Completa l'intervento in corso per riportare l'asset allo stato "Attivo".
            </p>
          </div>
          <div className="form-group">
            <label className="form-label">Descrizione intervento</label>
            <textarea
              value={completeForm.description}
              onChange={(e) => setCompleteForm({ ...completeForm, description: e.target.value })}
              className="input"
              rows={3}
              placeholder="Descrizione dell'intervento..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Condizione finale</label>
            <select
              value={completeForm.newCondition}
              onChange={(e) => setCompleteForm({ ...completeForm, newCondition: e.target.value })}
              className="input"
            >
              <option value="none">Nessun cambio</option>
              {Object.entries(conditionConfig).map(([value, config]) => (
                <option key={value} value={value}>{config.label}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              <Euro className="w-3.5 h-3.5" />
              Costo intervento (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={completeForm.cost || ''}
              onChange={(e) => setCompleteForm({ ...completeForm, cost: parseFloat(e.target.value) || 0 })}
              className="input"
              placeholder="0.00"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setCompleteInterventionModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleCompleteIntervention}
              disabled={updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <CheckCircle className="w-4 h-4" />
              Completa
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit Cost Modal */}
      <Modal isOpen={editCostModalOpen} onClose={() => setEditCostModalOpen(false)} title="Modifica Costo">
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label flex items-center gap-1">
              <Euro className="w-3.5 h-3.5" />
              Costo intervento (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={editCostForm.cost || ''}
              onChange={(e) => setEditCostForm({ ...editCostForm, cost: parseFloat(e.target.value) || 0 })}
              className="input"
              placeholder="0.00"
            />
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setEditCostModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleEditCost}
              disabled={updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              Salva
            </button>
          </div>
        </div>
      </Modal>

      {/* Move Completion Modal - Choose scan or complete directly */}
      <Modal isOpen={moveCompleteModalOpen} onClose={() => { setMoveCompleteModalOpen(false); setSelectedMoveAction(null); }} title="Completa Spostamento">
        <div className="space-y-4">
          {selectedMoveAction && (
            <>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-blue-800">
                      Destinazione: {selectedMoveAction.toLocationName}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      {selectedMoveAction.assetName}
                    </p>
                    {!selectedMoveAction.isOwnAction && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Azione avviata da un altro utente - sarà richiesta la scansione dell'asset
                      </p>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <button
                  onClick={startMoveQrScan}
                  className="w-full btn btn-primary flex items-center justify-center gap-2"
                >
                  <QrCode className="w-4 h-4" />
                  Scansiona QR Destinazione
                </button>
                
                <button
                  onClick={completeMoveDirectly}
                  disabled={updateMutation.isPending}
                  className="w-full btn btn-secondary flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle className="w-4 h-4" />
                  Completa Senza Scansione
                </button>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Scansiona il QR code della destinazione per verificare il corretto posizionamento dell'asset.
              </p>
            </>
          )}
        </div>
      </Modal>

      {/* Asset Scan Success Modal - Intermediate step */}
      {assetScanSuccess && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setAssetScanSuccess(false)} />
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-center align-middle bg-white rounded-2xl shadow-xl transform transition-all animate-slideInUp">
              <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 bg-emerald-100">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-emerald-800">
                Asset Verificato
              </h3>
              <p className="text-gray-600 mb-2">
                Hai l'asset corretto in possesso.
              </p>
              <p className="text-sm text-gray-500 mb-4">
                Ora scansiona il QR code della destinazione "{scanningMoveAction?.toLocationName}" per completare lo spostamento.
              </p>
              <button
                onClick={continueToLocationScan}
                className="w-full btn bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2"
              >
                <QrCode className="w-4 h-4" />
                Scansiona Destinazione
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR Scanner for Move Completion */}
      {qrScannerOpen && scanningMoveAction && (
        <QrScanner
          isOpen={qrScannerOpen}
          onClose={() => {
            setQrScannerOpen(false);
            setScanningMoveAction(null);
            setScanStep('location');
            setAssetVerified(false);
          }}
          onScanResult={handleQrScanResult}
          navigateOnScan={false}
          title={scanStep === 'asset' ? "Verifica Asset" : "Conferma Destinazione"}
          hint={scanStep === 'asset' 
            ? `📦 Scansiona il QR dell'asset "${asset.name}" per verificare di averlo in possesso.`
            : `📍 Scansiona il QR dell'ubicazione "${scanningMoveAction.toLocationName}" per completare lo spostamento.`
          }
        />
      )}

      {/* Scheduled Maintenance Modal */}
      <Modal 
        isOpen={scheduledMaintenanceModalOpen} 
        onClose={() => {
          setScheduledMaintenanceModalOpen(false);
          setEditingScheduledMaintenance(null);
          setScheduledMaintenanceForm({
            description: '',
            intervalMonths: 12,
            estimatedCost: '',
            referenceType: 'asset_creation',
            customReferenceDate: ''
          });
        }} 
        title={editingScheduledMaintenance ? "Modifica Manutenzione Programmata" : "Aggiungi Manutenzione Programmata"}
      >
        <div className="space-y-4">
          <div className="form-group">
            <label className="form-label">
              Descrizione <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={scheduledMaintenanceForm.description}
              onChange={(e) => setScheduledMaintenanceForm({ ...scheduledMaintenanceForm, description: e.target.value })}
              className="input"
              placeholder="es. Revisione annuale, Sostituzione filtri..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Intervallo <span className="text-red-500">*</span>
            </label>
            <select
              value={scheduledMaintenanceForm.intervalMonths}
              onChange={(e) => setScheduledMaintenanceForm({ ...scheduledMaintenanceForm, intervalMonths: parseInt(e.target.value) })}
              className="input"
            >
              <option value={1}>Ogni mese</option>
              <option value={2}>Ogni 2 mesi</option>
              <option value={3}>Ogni 3 mesi (trimestrale)</option>
              <option value={6}>Ogni 6 mesi (semestrale)</option>
              <option value={12}>Ogni anno</option>
              <option value={24}>Ogni 2 anni</option>
              <option value={36}>Ogni 3 anni</option>
              <option value={60}>Ogni 5 anni</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Costo Stimato (€)</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={scheduledMaintenanceForm.estimatedCost}
              onChange={(e) => setScheduledMaintenanceForm({ ...scheduledMaintenanceForm, estimatedCost: e.target.value })}
              className="input"
              placeholder="0.00 (opzionale)"
            />
          </div>

          <div className="form-group">
            <label className="form-label">
              Data di riferimento <span className="text-red-500">*</span>
            </label>
            <select
              value={scheduledMaintenanceForm.referenceType}
              onChange={(e) => setScheduledMaintenanceForm({ 
                ...scheduledMaintenanceForm, 
                referenceType: e.target.value as 'asset_creation' | 'purchase_date' | 'custom',
                customReferenceDate: e.target.value !== 'custom' ? '' : scheduledMaintenanceForm.customReferenceDate
              })}
              className="input"
            >
              <option value="asset_creation">Data inserimento asset</option>
              <option value="purchase_date">Data acquisto</option>
              <option value="custom">Data personalizzata</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">
              La prossima manutenzione sarà calcolata a partire da questa data
            </p>
          </div>

          {scheduledMaintenanceForm.referenceType === 'custom' && (
            <div className="form-group">
              <label className="form-label">
                Data personalizzata <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                value={scheduledMaintenanceForm.customReferenceDate}
                onChange={(e) => setScheduledMaintenanceForm({ ...scheduledMaintenanceForm, customReferenceDate: e.target.value })}
                className="input"
              />
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setScheduledMaintenanceModalOpen(false);
                setEditingScheduledMaintenance(null);
              }}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={editingScheduledMaintenance ? handleUpdateScheduledMaintenance : handleAddScheduledMaintenance}
              disabled={
                updateMutation.isPending || 
                !scheduledMaintenanceForm.description.trim() ||
                (scheduledMaintenanceForm.referenceType === 'custom' && !scheduledMaintenanceForm.customReferenceDate)
              }
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {editingScheduledMaintenance ? 'Salva Modifiche' : 'Aggiungi'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Result Modal */}
      {resultModal.isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setResultModal({ ...resultModal, isOpen: false })} />
            <div className="relative inline-block w-full max-w-md p-6 my-8 text-center align-middle bg-white rounded-2xl shadow-xl transform transition-all animate-slideInUp">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4 ${
                resultModal.type === 'success' ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {resultModal.type === 'success' ? (
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                ) : (
                  <XCircle className="w-8 h-8 text-red-600" />
                )}
              </div>
              <h3 className={`text-xl font-bold mb-2 ${
                resultModal.type === 'success' ? 'text-emerald-800' : 'text-red-800'
              }`}>
                {resultModal.title}
              </h3>
              <p className="text-gray-600 mb-2">{resultModal.message}</p>
              {resultModal.details && (
                <p className="text-sm text-gray-500 mb-4">{resultModal.details}</p>
              )}
              <button
                onClick={() => setResultModal({ ...resultModal, isOpen: false })}
                className={`w-full btn ${
                  resultModal.type === 'success' 
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
